import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseViewSettings } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import { createEntry, createField } from '@databases/utils/database/utils';
import { getHiddenFields } from '@databases/utils/database/view/view';
import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { batch } from '@databases/utils/redux/redux';
import { generateId } from '@databases/utils/utils';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';

import { DefaultRootState } from '../..';
import { deselectAll, selectColumns, selectEntries } from '../cellSelections/actions';
import {
  selectSelectedEntireEntryIds,
  selectSelectedEntireFieldIds,
  selectSelectedEntryIds,
  selectSelectedFieldIds,
} from '../cellSelections/selectors';
import { addChangeToDraft } from '../changesets/actions';
import { selectDatabaseDraftByContentId } from '../changesets/selectors';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

export const doInsertEntries =
  (
    contentId: string,
    scope: string,
    viewSettings: DatabaseViewSettings | undefined,
    insertBefore: boolean
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const database = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    const entry = createEntry(database, viewSettings);

    const selectedEntryIds = selectSelectedEntryIds(getState(), {
      contentId,
      scope,
      viewSettings,
    });
    if (selectedEntryIds.length === 0) {
      return;
    }

    const aboveOrBelowSelectedEntry = insertBefore
      ? selectedEntryIds[0]
      : selectedEntryIds[selectedEntryIds.length - 1];

    const index = database.entries.indexOf(aboveOrBelowSelectedEntry);
    dispatch(
      addChangeToDraft(contentId, scope, {
        type: DatabaseChangeType.ADD_ENTRY,
        entry,
        index: insertBefore ? index : index + 1,
      })
    );
    dispatch(deselectAll());
  };

export const doDuplicateEntireEntries =
  (
    contentId: string,
    scope: string,
    viewSettings: DatabaseViewSettings | undefined
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    dispatch(
      addChangeToDraft(contentId, scope, {
        type: DatabaseChangeType.CLONE_ENTRIES,
        entryIds: Object.fromEntries(
          selectSelectedEntireEntryIds(getState(), {
            contentId,
            scope,
            viewSettings,
          }).map((entryId) => [entryId, generateId()])
        ),
      })
    );
  };

export const doClearEntireEntries =
  (
    contentId: string,
    scope: string,
    viewSettings: DatabaseViewSettings | undefined
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedEntireEntryIds = selectSelectedEntireEntryIds(getState(), {
      contentId,
      scope,
      viewSettings,
    });
    dispatch(
      addChangeToDraft(contentId, scope, {
        type: DatabaseChangeType.CLEAR_ENTRIES,
        entryIds: selectedEntireEntryIds,
      })
    );
  };

export const doDeleteEntries =
  (
    contentId: string,
    scope: string,
    viewSettings: DatabaseViewSettings | undefined
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedEntryIds = selectSelectedEntryIds(getState(), {
      contentId,
      scope,
      viewSettings,
    });
    batch(() => {
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        selectedEntryIds.forEach((entryId) => {
          dispatch(
            addChangeToDraft(contentId, scope, {
              type: DatabaseChangeType.DELETE_ENTRY,
              entryId: entryId,
            })
          );
        });
      });
    });
  };

export const doSelectNewEntries =
  (contentId: string, callback: () => Promise<void> | void): Dispatcher<Promise<void>> =>
  async (dispatch, getState) => {
    const database = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    await callback();
    const newDatabase = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    const newEntryIds = newDatabase.entries.filter(
      (id) => !database.entries.includes(id)
    );
    if (newEntryIds.length > 0) {
      batch(() => {
        dispatch(
          selectEntries({
            entryIds: newEntryIds,
          })
        );
      });
    }
  };

export const doMoveEntries =
  (
    contentId: string,
    scope: string,
    viewSettings: DatabaseViewSettings | undefined,
    moveUp: boolean
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedEntryIds = selectSelectedEntryIds(getState(), {
      contentId,
      scope,
      viewSettings,
    });
    const databaseDraft = selectDatabaseDraftByContentId(getState(), {
      scope,
      contentId,
      viewSettings,
      includeDeleted: true,
    });
    const firstOrLastEntryId = moveUp
      ? selectedEntryIds[0]
      : selectedEntryIds[selectedEntryIds.length - 1];
    const currentIndexInEntryIds = databaseDraft.entries.indexOf(firstOrLastEntryId);
    batch(() => {
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        dispatch(
          addChangeToDraft(contentId, scope, {
            type: DatabaseChangeType.REORDER_ENTRIES,
            entryIds: selectedEntryIds,
            newIndex: moveUp ? currentIndexInEntryIds - 1 : currentIndexInEntryIds + 2,
          })
        );
      });
    });
  };

export const doInsertFields =
  (
    contentId: string,
    scope: string,
    viewSettings: DatabaseViewSettings | undefined,
    currentViewId: string,
    insertBefore: boolean
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const database = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    const newField = createField(DatabaseFieldType.TEXT);

    const selectedFieldIds = selectSelectedFieldIds(getState(), {
      contentId,
      scope,
      viewSettings,
    });
    if (selectedFieldIds.length === 0) {
      return;
    }

    const beforeOrAfterSelectedField = insertBefore
      ? selectedFieldIds[0]
      : selectedFieldIds[selectedFieldIds.length - 1];

    const index = database.fields.indexOf(beforeOrAfterSelectedField);
    dispatch(
      addChangeToDraft(contentId, scope, {
        type: DatabaseChangeType.ADD_FIELD,
        field: newField,
        index: insertBefore ? index : index + 1,
        currentViewId,
      })
    );
    dispatch(deselectAll());
  };

export const doDuplicateEntireFields =
  (
    contentId: string,
    scope: string,
    viewSettings: DatabaseViewSettings | undefined,
    currentViewId: string
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedEntireFieldIds = selectSelectedEntireFieldIds(getState(), {
      contentId,
      scope,
      viewSettings,
    });
    dispatch(
      addChangeToDraft(contentId, scope, {
        type: DatabaseChangeType.CLONE_FIELDS,
        fieldIds: Object.fromEntries(
          selectedEntireFieldIds.map((fieldId) => [fieldId, generateId()])
        ),
        currentViewId,
      })
    );
  };

export const doClearEntireFields =
  (
    contentId: string,
    scope: string,
    viewSettings: DatabaseViewSettings | undefined
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedEntireFieldIds = selectSelectedEntireFieldIds(getState(), {
      contentId,
      scope,
      viewSettings,
    });
    dispatch(
      addChangeToDraft(contentId, scope, {
        type: DatabaseChangeType.CLEAR_ENTRIES,
        fieldIds: selectedEntireFieldIds,
      })
    );
  };

export const doDeleteFields =
  (
    contentId: string,
    scope: string,
    viewSettings: DatabaseViewSettings | undefined
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedFieldIds = selectSelectedFieldIds(getState(), {
      contentId,
      scope,
      viewSettings,
    });
    batch(() => {
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        selectedFieldIds.forEach((fieldId) => {
          dispatch(
            addChangeToDraft(contentId, scope, {
              type: DatabaseChangeType.DELETE_FIELD,
              fieldId,
            })
          );
        });
      });
    });
  };

export const doMoveFields =
  (
    contentId: string,
    scope: string,
    viewSettings: DatabaseViewSettings | undefined,
    moveLeft: boolean
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedFieldIds = selectSelectedFieldIds(getState(), {
      contentId,
      scope,
      viewSettings,
    });
    const databaseView = selectDatabaseDraftByContentId(getState(), {
      scope,
      contentId,
      viewSettings,
      includeDeleted: true,
    });
    const hiddenFields = getHiddenFields(viewSettings);
    batch(() => {
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        selectedFieldIds.forEach((fieldId, idx) => {
          const currentIndexInFields = databaseView.fields.indexOf(fieldId);
          dispatch(
            addChangeToDraft(contentId, scope, {
              type: DatabaseChangeType.REORDER_FIELD,
              fieldId,
              hiddenFields,
              newIndex: moveLeft
                ? currentIndexInFields - 1
                : currentIndexInFields + selectedFieldIds.length - idx + 1,
            })
          );
        });
      });
    });
  };

export const doSelectNewFields =
  (contentId: string, callback: () => Promise<void> | void): Dispatcher<Promise<void>> =>
  async (dispatch, getState) => {
    const database = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    await callback();
    const newDatabase = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    const newFieldIds = newDatabase.fields.filter((id) => !database.fields.includes(id));
    if (newFieldIds.length > 0) {
      batch(() => {
        dispatch(
          selectColumns({
            fieldIds: newFieldIds,
          })
        );
      });
    }
  };

export const doClearValues =
  (
    contentId: string,
    scope: string,
    viewSettings: DatabaseViewSettings | undefined
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedEntryIds = selectSelectedEntryIds(getState(), {
      contentId,
      scope,
      viewSettings,
    });
    const selectedFieldIds = selectSelectedFieldIds(getState(), {
      contentId,
      scope,
      viewSettings,
    });
    dispatch(
      addChangeToDraft(contentId, scope, {
        type: DatabaseChangeType.CLEAR_ENTRIES,
        entryIds: selectedEntryIds,
        fieldIds: selectedFieldIds,
      })
    );
  };
